import type { Sim } from '../../game/simulation';

/**
 * Base class for every side / overlay panel.
 *
 * Subclasses implement {@link id}, {@link title}, {@link body}, and
 * {@link hashInputs}. The base handles mount/unmount, show/hide, and a
 * hash-diff render so the DOM is only rebuilt when the panel's inputs
 * actually change. A single delegated click handler on the panel root
 * survives the per-frame rebuilds — subclasses override {@link onClick}.
 */
export abstract class Panel {
  protected root: HTMLElement;
  private lastHash = '';
  private mounted = false;
  private busUnsub: (() => void) | null = null;

  constructor(protected sim: Sim) {
    this.root = document.createElement('section');
    this.root.className = `ui-panel ui-panel--${this.id()}`;
    this.root.style.display = 'none';
    this.root.addEventListener('click', this.onRootClick);
  }

  /** Stable identifier — used as a CSS hook and by PanelManager. */
  abstract id(): string;
  /** Title shown in the panel header. */
  abstract title(): string;
  /** Inner HTML for the panel body (excluding the auto-rendered header). */
  abstract body(): string;
  /** Hash of inputs the panel renders from. Identical hash → no re-render. */
  abstract hashInputs(): string;

  /** Override to handle click events that bubble to the panel root. */
  protected onClick(_e: Event): void {}
  /** Called when the close button is pressed. */
  protected onClose(): void {}

  mount(parent: HTMLElement) {
    if (this.mounted) return;
    parent.appendChild(this.root);
    this.mounted = true;
    this.render(true);
  }

  unmount() {
    if (!this.mounted) return;
    this.root.remove();
    this.mounted = false;
    this.busUnsub?.();
    this.busUnsub = null;
  }

  show() {
    if (this.root.style.display !== '') {
      this.root.style.display = '';
      this.root.classList.add('ui-panel--open');
    }
    this.tick();
  }

  hide() {
    this.root.style.display = 'none';
    this.root.classList.remove('ui-panel--open');
  }

  isVisible(): boolean {
    return this.mounted && this.root.style.display !== 'none';
  }

  /** Re-render if inputs changed. Called every frame by PanelManager. */
  tick() {
    if (!this.isVisible()) return;
    this.render(false);
  }

  private render(force: boolean) {
    const h = this.hashInputs();
    if (!force && h === this.lastHash) return;
    this.lastHash = h;
    // Preserve scroll position across innerHTML rebuilds. Without this,
    // the panel body's scrollTop snaps to 0 every time hashInputs()
    // changes — which can fire several times per second when items
    // are flying in or buttons are being clicked. Reads as the scroller
    // "jumping to the top" while the player is mid-interaction.
    const oldBody = this.root.querySelector<HTMLElement>('.ui-panel__body');
    const prevScrollTop = oldBody?.scrollTop ?? 0;
    this.root.innerHTML = `
      <header class="ui-panel__header">
        <h2 class="ui-panel__title">${this.title()}</h2>
        <button class="ui-panel__close" data-close aria-label="Close">×</button>
      </header>
      <div class="ui-panel__body">${this.body()}</div>
    `;
    if (prevScrollTop > 0) {
      const newBody = this.root.querySelector<HTMLElement>('.ui-panel__body');
      if (newBody) newBody.scrollTop = prevScrollTop;
    }
  }

  private onRootClick = (e: Event) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close]')) {
      this.hide();
      this.onClose();
      return;
    }
    this.onClick(e);
  };
}
